import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';
import { ROUTES } from '@/constants/routes';
import { useMyMembership } from '@/hooks/useMyMembership';
import { USERS_SELECT_COLUMNS } from '@/constants/userColumns';

export interface ProfileChecklistItem {
  id: string;
  labelKey: string;
  defaultLabel: string;
  completed: boolean;
  route: string;
  /** CTA button label key */
  ctaLabelKey: string;
  ctaDefaultLabel: string;
}

interface ProfileCompletion {
  isComplete: boolean;
  missingFields: string[];
  completionPercentage: number;
  /** Grouped checklist items for the unified card */
  checklistItems: ProfileChecklistItem[];
  /** Whether the user has an active membership */
  hasActiveMembership: boolean;
}

async function fetchProfileForCompletion(userId: string): Promise<Record<string, unknown> | null> {
  return db.fetchOne<Record<string, unknown>>('users', USERS_SELECT_COLUMNS, [
    { column: 'id', operator: 'eq', value: userId }
  ]);
}

export function useProfileCompletion() {
  const { user, session } = useAuth();
  const { data: membership, isLoading: membershipLoading } = useMyMembership();

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: queryKeys.users.profile(user?.id ?? ''),
    queryFn: () => fetchProfileForCompletion(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const profileStatus = useMemo<ProfileCompletion>(() => {
    if (!profile) {
      return {
        isComplete: true,
        missingFields: [],
        completionPercentage: 100,
        checklistItems: [],
        hasActiveMembership: !!membership,
      };
    }

    const isEmpty = (key: string) => {
      const value = profile[key];
      return !value || (typeof value === 'string' && value.trim() === '');
    };

    const missing: string[] = [];
    const items: ProfileChecklistItem[] = [];

    // 1. Email verification
    const emailVerified = !!session?.user?.email_confirmed_at;
    if (!emailVerified) missing.push('labels.email_verified');
    items.push({
      id: 'email_verified',
      labelKey: 'dashboard.checklist.verify_email',
      defaultLabel: 'Verify your email',
      completed: emailVerified,
      route: ROUTES.ACCOUNT_SETTINGS,
      ctaLabelKey: 'actions.verify',
      ctaDefaultLabel: 'Verify',
    });

    // 2. Profile photo
    const hasPhoto = !isEmpty('image');
    if (!hasPhoto) missing.push('labels.profile_picture');
    items.push({
      id: 'profile_photo',
      labelKey: 'dashboard.checklist.add_photo',
      defaultLabel: 'Add a profile photo',
      completed: hasPhoto,
      route: ROUTES.ACCOUNT_PROFILE,
      ctaLabelKey: 'actions.upload',
      ctaDefaultLabel: 'Upload',
    });

    // 3. Name fields (first, last, display) — grouped
    const hasName = !isEmpty('name') && !isEmpty('lastname') && !isEmpty('display_name');
    if (!hasName) missing.push('labels.display_name');
    items.push({
      id: 'name_fields',
      labelKey: 'dashboard.checklist.complete_name',
      defaultLabel: 'Complete your name',
      completed: hasName,
      route: ROUTES.ACCOUNT_PROFILE,
      ctaLabelKey: 'actions.complete',
      ctaDefaultLabel: 'Complete',
    });

    // 4. Bio
    const hasBio = !isEmpty('about');
    if (!hasBio) missing.push('labels.bio_about');
    items.push({
      id: 'bio',
      labelKey: 'dashboard.checklist.write_bio',
      defaultLabel: 'Write your professional bio',
      completed: hasBio,
      route: ROUTES.ACCOUNT_PROFILE,
      ctaLabelKey: 'actions.write',
      ctaDefaultLabel: 'Write',
    });

    // 5. Vanity URL
    const hasVanity = !isEmpty('vanity_url');
    if (!hasVanity) missing.push('labels.vanity_url');
    items.push({
      id: 'vanity_url',
      labelKey: 'dashboard.checklist.set_vanity_url',
      defaultLabel: 'Set your vanity URL',
      completed: hasVanity,
      route: ROUTES.ACCOUNT_PROFILE,
      ctaLabelKey: 'actions.set',
      ctaDefaultLabel: 'Set',
    });

    // 6. LinkedIn
    const hasLinkedIn = !isEmpty('linkedin');
    if (!hasLinkedIn) missing.push('labels.linkedin_profile');
    items.push({
      id: 'linkedin',
      labelKey: 'dashboard.checklist.add_linkedin',
      defaultLabel: 'Add LinkedIn profile',
      completed: hasLinkedIn,
      route: ROUTES.ACCOUNT_PROFILE,
      ctaLabelKey: 'actions.add',
      ctaDefaultLabel: 'Add',
    });

    // 7. Contact details (phone, website) — grouped
    const hasContact = !isEmpty('telephone') && !isEmpty('website');
    if (isEmpty('telephone')) missing.push('labels.phone_number');
    if (isEmpty('website')) missing.push('labels.website');
    items.push({
      id: 'contact_details',
      labelKey: 'dashboard.checklist.add_contact',
      defaultLabel: 'Add contact details',
      completed: hasContact,
      route: ROUTES.ACCOUNT_PROFILE,
      ctaLabelKey: 'actions.complete',
      ctaDefaultLabel: 'Complete',
    });

    // 8. Address (country, state, city, postal, street) — grouped
    const addressFields = ['country', 'state_province', 'city', 'postal_zip_code', 'address_1'];
    const hasAddress = addressFields.every(f => !isEmpty(f));
    addressFields.forEach(f => {
      if (isEmpty(f)) missing.push(`labels.${f}`);
    });
    items.push({
      id: 'address',
      labelKey: 'dashboard.checklist.add_address',
      defaultLabel: 'Add your address',
      completed: hasAddress,
      route: ROUTES.ACCOUNT_PROFILE,
      ctaLabelKey: 'actions.complete',
      ctaDefaultLabel: 'Complete',
    });

    // Business field (conditional)
    if (profile.type === 'business') {
      const hasBusiness = !isEmpty('business');
      if (!hasBusiness) missing.push('labels.business_company');
      items.push({
        id: 'business',
        labelKey: 'dashboard.checklist.add_business',
        defaultLabel: 'Add business/company',
        completed: hasBusiness,
        route: ROUTES.ACCOUNT_PROFILE,
        ctaLabelKey: 'actions.add',
        ctaDefaultLabel: 'Add',
      });
    }

    // Calculate completion based on checklist items
    const totalItems = items.length;
    const completedItems = items.filter(i => i.completed).length;
    let completionPercentage = totalItems > 0
      ? Math.round((completedItems / totalItems) * 100)
      : 100;

    // Cap at 50% if user has no active membership
    const hasActiveMembership = !!membership;
    if (!hasActiveMembership) {
      completionPercentage = Math.min(completionPercentage, 50);
    }

    return {
      isComplete: missing.length === 0 && hasActiveMembership,
      missingFields: missing,
      completionPercentage,
      checklistItems: items,
      hasActiveMembership,
    };
  }, [profile, session?.user?.email_confirmed_at, membership]);

  return { ...profileStatus, isLoading: profileLoading || membershipLoading };
}
